import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard, 
  Truck, 
  Package, 
  Search, 
  MessageSquare, 
  MapPin, 
  LogOut,
  Menu,
  X,
  Bell,
  ChevronRight,
  Shield,
  HeadphonesIcon,
  Settings,
  FileWarning,
  BarChart3
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import LanguageSwitcher from './LanguageSwitcher';

const Layout = ({ children }) => {
  const { t } = useTranslation();
  const { user, logout, isEnterprise, isCarrier } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const enterpriseNavItems = [
    { path: '/dashboard', icon: LayoutDashboard, labelKey: 'nav.dashboard' },
    { path: '/missions', icon: Package, labelKey: 'nav.missions' },
    { path: '/smartmatch', icon: Search, labelKey: 'nav.smartmatch' },
    { path: '/tracking', icon: MapPin, labelKey: 'nav.tracking' },
    { path: '/messages', icon: MessageSquare, labelKey: 'nav.messages' },
    { path: '/kyc', icon: Shield, labelKey: 'nav.kyc' },
    { path: '/aml', icon: FileWarning, labelKey: 'nav.aml' },
    { path: '/analytics', icon: BarChart3, labelKey: 'nav.analytics' },
    { path: '/support', icon: HeadphonesIcon, labelKey: 'nav.support' },
    { path: '/admin', icon: Settings, labelKey: 'nav.admin' },
  ];

  const carrierNavItems = [
    { path: '/dashboard', icon: LayoutDashboard, labelKey: 'nav.dashboard' },
    { path: '/opportunities', icon: Package, labelKey: 'nav.opportunities' },
    { path: '/fleet', icon: Truck, labelKey: 'nav.fleet' },
    { path: '/my-bids', icon: ChevronRight, labelKey: 'nav.myBids' },
    { path: '/messages', icon: MessageSquare, labelKey: 'nav.messages' },
    { path: '/kyc', icon: Shield, labelKey: 'nav.kyc' },
    { path: '/support', icon: HeadphonesIcon, labelKey: 'nav.support' },
  ];

  const navItems = isEnterprise ? enterpriseNavItems : carrierNavItems;

  return (
    <div className="app-layout min-h-screen">
      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-16 bg-slate-900 z-40 flex items-center justify-between px-4">
        <button 
          onClick={() => setSidebarOpen(true)}
          className="text-white p-2"
          data-testid="mobile-menu-btn"
        >
          <Menu className="w-6 h-6" />
        </button>
        <span className="text-white font-bold text-lg font-['Chivo']">Flitflow</span>
        <div className="flex items-center gap-2">
          <LanguageSwitcher variant="dark" />
          <button className="text-white p-2 relative">
            <Bell className="w-5 h-5" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-orange-500 rounded-full"></span>
          </button>
        </div>
      </div>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed md:static top-0 left-0 h-full w-64 bg-slate-900 z-50 transform transition-transform duration-300
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-slate-800">
            <div className="flex items-center justify-between">
              <Link to="/dashboard" className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
                  <Truck className="w-6 h-6 text-white" />
                </div>
                <span className="text-white font-bold text-xl font-['Chivo'] tracking-tight">Flitflow</span>
              </Link>
              <button 
                onClick={() => setSidebarOpen(false)}
                className="md:hidden text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* User Info */}
          <div className="px-4 py-4 border-b border-slate-800">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center text-white font-medium">
                {user?.company_name?.[0]?.toUpperCase() || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{user?.company_name}</p>
                <Badge variant="outline" className="mt-1 text-xs text-slate-400 border-slate-700">
                  {isEnterprise ? t('auth.roleEnterprise') : t('auth.roleCarrier')}
                </Badge>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-4 overflow-y-auto">
            <ul className="space-y-1 px-2">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      className={`
                        flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200
                        ${isActive 
                          ? 'bg-orange-500 text-white' 
                          : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                        }
                      `}
                      data-testid={`nav-${item.path.replace('/', '')}`}
                    >
                      <item.icon className="w-5 h-5" />
                      <span className="font-medium">{t(item.labelKey)}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Language Switcher in Sidebar */}
          <div className="px-4 py-2 border-t border-slate-800 hidden md:block">
            <LanguageSwitcher variant="dark" />
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-slate-800">
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 w-full px-4 py-3 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-all"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-medium">{t('nav.logout')}</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 md:ml-0 mt-16 md:mt-0 min-h-screen bg-slate-50">
        <div className="max-w-7xl mx-auto p-4 md:p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
